/**
 * RAG Notion — busca semântica simples no conteúdo das páginas Notion.
 *
 * Estratégia (lite, client-side):
 * 1. Busca páginas via Notion Search API com a query
 * 2. Para top N, busca os blocos de texto
 * 3. Devolve trechos relevantes pra injetar no contexto da geração
 *
 * Futuro: indexar via pgvector + embeddings (já temos pgvector_postgres rodando).
 * Por ora esta versão usa Notion Search nativa, que já faz matching textual decente.
 */

const NOTION_API = "https://api.notion.com/v1";

export interface NotionHit {
  pageId: string;
  title: string;
  url?: string;
  text: string;            // trechos concatenados
  excerptScore?: number;
}

async function notionFetch(token: string, path: string, init?: RequestInit) {
  const res = await fetch(`${NOTION_API}${path}`, {
    ...init,
    headers: {
      "authorization": `Bearer ${token}`,
      "notion-version": "2022-06-28",
      "content-type": "application/json",
      ...(init?.headers ?? {})
    }
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Notion ${res.status}: ${t.slice(0, 200)}`);
  }
  return res.json();
}

/** Busca top N páginas via Notion Search nativa. */
export async function notionSearch(token: string, query: string, limit = 5): Promise<NotionHit[]> {
  const result = await notionFetch(token, "/search", {
    method: "POST",
    body: JSON.stringify({
      query,
      filter: { value: "page", property: "object" },
      page_size: limit
    })
  });

  const pages = result.results ?? [];
  const hits: NotionHit[] = [];
  for (const page of pages.slice(0, limit)) {
    try {
      const title = extractTitle(page);
      const url = page.url;
      const text = await fetchPageText(token, page.id, 2500);
      hits.push({ pageId: page.id, title, url, text });
    } catch (e) {
      // ignora páginas que falham — algumas dão 403 em DBs privados
    }
  }
  return hits;
}

/** Concatena texto dos primeiros blocos de uma página. */
async function fetchPageText(token: string, pageId: string, maxChars: number): Promise<string> {
  const result = await notionFetch(token, `/blocks/${pageId}/children?page_size=50`);
  const parts: string[] = [];
  let total = 0;
  for (const block of result.results ?? []) {
    const t = blockToText(block);
    if (t) {
      parts.push(t);
      total += t.length;
      if (total >= maxChars) break;
    }
  }
  return parts.join("\n").slice(0, maxChars);
}

function blockToText(block: any): string {
  const type = block.type;
  if (!type) return "";
  const rt = block[type]?.rich_text;
  if (Array.isArray(rt)) {
    const t = rt.map((r: any) => r.plain_text ?? "").join("");
    if (type === "heading_1") return `\n# ${t}`;
    if (type === "heading_2") return `\n## ${t}`;
    if (type === "heading_3") return `\n### ${t}`;
    if (type === "bulleted_list_item") return `- ${t}`;
    if (type === "numbered_list_item") return `1. ${t}`;
    if (type === "to_do") return `[${block[type].checked ? "x" : " "}] ${t}`;
    if (type === "code") return `\`${t}\``;
    if (type === "quote") return `> ${t}`;
    return t;
  }
  return "";
}

function extractTitle(page: any): string {
  // tentar properties.title.title OR properties.Name.title OR primeiro property title-like
  const props = page.properties ?? {};
  for (const key of ["title", "Title", "Name", "name", "Nome"]) {
    const p = props[key];
    if (p?.title?.length) return p.title.map((r: any) => r.plain_text).join("");
  }
  for (const v of Object.values(props) as any[]) {
    if (v?.title?.length) return v.title.map((r: any) => r.plain_text).join("");
  }
  return "(sem título)";
}

/** Monta um bloco de contexto formatado pra injetar em prompt. */
export function formatRagContext(hits: NotionHit[]): string {
  if (hits.length === 0) return "";
  return `\n\n[CONTEXTO DO NOTION — informações da base de conhecimento]\n\n` +
    hits.map((h, i) =>
      `## ${i + 1}. ${h.title}\n${h.text.slice(0, 800)}${h.text.length > 800 ? "…" : ""}`
    ).join("\n\n");
}

/** Ping de validação do token. */
export async function notionPing(token: string): Promise<boolean> {
  try {
    await notionFetch(token, "/users/me");
    return true;
  } catch {
    return false;
  }
}
